/**
 * AIZEn ad click-tracking composable.
 *
 * Provides three utilities:
 *
 * 1. withAdReferrerUtm(url)
 *    Simple backwards-compatible wrapper that appends `utm_term=<page host>` to
 *    an AIZEn click URL.  Drop-in for the basic case where no per-placement
 *    context is available.
 *
 * 2. buildAdClickUrl(url, ctx)
 *    Richer variant that appends the full attribution context the AIZEn click
 *    endpoint understands: page key, post ID, ad surface, visitor ID, and a
 *    compact first-touch summary.  The `utm_term` param is also set so
 *    existing Click Sources reporting in AIZEn continues to work unchanged.
 *
 * 3. withUtmContent(url, pageKey) / useCampaignUrl(url)
 *    Appends `utm_content=<pageKey>` to a destination URL when the param is
 *    not already present.  For hardcoded CTAs that skip the AIZEn click
 *    tracker, so the LP still receives the page identifier.
 *
 * Context params appended to the click URL:
 *   utm_term — compact page host (hostname + path, no protocol/TLD/slashes)
 *   pg        — page key  e.g. "ts.blog.how-to-short-a-stock"
 *   pid       — WordPress post/page ID
 *   sf        — ad surface / placement  e.g. "eoa", "sidebar", "banner"
 *   vid       — anonymous visitor UUID from _aiz_vid cookie
 *   ft        — first-touch campaign code from _aiz_ft cookie (compact)
 *
 * WHY client-only: AIZEn ads are always fetched client-side, and the context
 * values (page URL, cookies) are only available in the browser.
 */

const COOKIE_VID = '_aiz_vid'
const COOKIE_FT  = '_aiz_ft'

/**
 * Compact page identifier for `utm_term`.
 *
 * https://stockstotrade.com/news/aveannahealthcareholdingsinc-avah-news-2026_08_16/
 *   → stockstotrade.news.aveannahealthcareholdingsinc-avah-news-2026_08_16
 *
 * Drops protocol, common TLDs (.com / .dev), query, hash, and slashes.
 */
export function toUtmTermHost(href) {
  try {
    const url = new URL(href)
    const host = url.hostname.replace(/\.(com|dev)$/i, '')
    const path = url.pathname.replace(/^\/+|\/+$/g, '').replace(/\//g, '.')
    return path ? `${host}.${path}` : host
  } catch {
    return ''
  }
}

function currentUtmTerm() {
  return toUtmTermHost(window.location.href)
}

function readCookie(name) {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(
    new RegExp('(?:^|;\\s*)' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)')
  )
  return match ? decodeURIComponent(match[1]) : null
}

/**
 * Read the first-touch cookie and return the utm_campaign value (or empty
 * string).  This is the most useful single field for CRM matching: it carries
 * the acquisition campaign code from the visitor's very first site visit.
 */
function readFirstTouchCampaign() {
  try {
    const raw = readCookie(COOKIE_FT)
    if (!raw) return ''
    const ft = JSON.parse(raw)
    return ft.utmCampaign || ft.utmSource || ''
  } catch {
    return ''
  }
}

/**
 * Appends attribution context to an AIZEn ad click URL.
 *
 * @param {string | null | undefined} url  Resolved ad click URL
 * @param {{ pageKey?: string, postId?: string, surface?: string }} [ctx]
 *   Optional page/placement context from usePageKey() and the component.
 * @returns {string | null | undefined}
 */
export function buildAdClickUrl(url, ctx) {
  if (!url || url === '#') return url
  if (!process.client) return url

  try {
    const resolved = new URL(url, window.location.origin)

    resolved.searchParams.set('utm_term', currentUtmTerm())

    if (ctx?.pageKey)  resolved.searchParams.set('pg',  ctx.pageKey)
    if (ctx?.postId)   resolved.searchParams.set('pid', String(ctx.postId))
    if (ctx?.surface)  resolved.searchParams.set('sf',  ctx.surface)

    if (ctx?.pageKey && !resolved.searchParams.get('utm_content')) {
      resolved.searchParams.set('utm_content', ctx.pageKey)
    }

    const vid = readCookie(COOKIE_VID)
    if (vid) resolved.searchParams.set('vid', vid)

    const ft = readFirstTouchCampaign()
    if (ft) resolved.searchParams.set('ft', ft)

    return resolved.toString()
  } catch {
    return url
  }
}

/**
 * Fires an `aiz_ad_click` event to the GTM dataLayer.
 *
 * Call this from the actual `@click` handler on ad CTA links, not from a
 * computed property.  Safe to call if GTM / dataLayer is not present.
 *
 * @param {{ pageKey?: string, postId?: string, surface?: string }} [ctx]
 */
export function fireAdClickEvent(ctx) {
  if (!process.client) return
  try {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event:        'aiz_ad_click',
      aiz_page:     ctx?.pageKey   || '',
      aiz_postId:   ctx?.postId    || '',
      aiz_surface:  ctx?.surface   || '',
      aiz_vid:      readCookie(COOKIE_VID) || '',
      aiz_ft:       readFirstTouchCampaign(),
      aiz_page_url: window.location.href,
    })
  } catch {
    // dataLayer push must never throw
  }
}

/**
 * Appends `utm_content=<pageKey>` to a destination URL when the param is not
 * already present (fill-if-empty, matching AIZEn's outbound policy).
 * Safe on SSR so hardcoded CTAs carry the page key in the initial HTML.
 *
 * @param {string | null | undefined} url
 * @param {string | null | undefined} pageKey  e.g. "ts.home", "ts.news.some-slug"
 * @returns {string | null | undefined}
 */
export function withUtmContent(url, pageKey) {
  if (!url || url === '#' || !pageKey) return url

  try {
    const resolved = new URL(url)
    if (!resolved.searchParams.get('utm_content')) {
      resolved.searchParams.set('utm_content', pageKey)
    }
    return resolved.toString()
  } catch {
    return url
  }
}

/**
 * Reactive wrapper around withUtmContent() for hardcoded campaign CTAs.
 *
 * @param {string | import('vue').Ref<string>} baseUrl
 * @returns {import('vue').ComputedRef<string>}
 */
export function useCampaignUrl(baseUrl) {
  const { pageKey } = usePageKey()
  return computed(() => withUtmContent(unref(baseUrl), pageKey.value))
}

/**
 * Backwards-compatible wrapper: appends only `utm_term=<page host>`.
 * Components that don't have post/placement context yet can keep using this.
 * Prefer buildAdClickUrl() when the full context is available.
 *
 * @param {string | null | undefined} url
 * @returns {string | null | undefined}
 */
export function withAdReferrerUtm(url) {
  if (!url || url === '#') return url
  if (!process.client) return url

  try {
    const resolved = new URL(url, window.location.origin)
    resolved.searchParams.set('utm_term', currentUtmTerm())
    return resolved.toString()
  } catch {
    return url
  }
}
